import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { settingsApi } from '@/lib/api/settings.api'
import {
  UpdateProfileRequest,
  ChangePasswordRequest,
  UpdateNotificationSettingsRequest,
} from '@/lib/types/settings.types'
import { toast } from '@/hooks/use-toast'

/**
 * Query key factory for settings
 */
export const settingsKeys = {
  all: ['settings'] as const,
  profile: () => [...settingsKeys.all, 'profile'] as const,
  notifications: () => [...settingsKeys.all, 'notifications'] as const,
}

/**
 * Hook to fetch educator profile
 * @param options - Optional query options (e.g., enabled)
 */
export function useProfile(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: settingsKeys.profile(),
    queryFn: () => settingsApi.getProfile(),
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  })
}

/**
 * Hook to update educator profile
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateProfileRequest) => settingsApi.updateProfile(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.profile() })
      toast({
        title: response.message.title,
        description: response.message.description,
      })
    },
    onError: (error: any) => {
      toast({
        title: error.response?.data?.message?.title || 'Error',
        description:
          error.response?.data?.message?.description ||
          'Failed to update profile. Please try again.',
        variant: 'destructive',
      })
    },
  })
}

/**
 * Hook to upload profile picture
 */
export function useUploadProfilePicture() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (file: File) => settingsApi.uploadProfilePicture(file),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.profile() })
      toast({
        title: response.message.title,
        description: response.message.description,
      })
    },
    onError: (error: any) => {
      toast({
        title: error.response?.data?.message?.title || 'Error',
        description:
          error.response?.data?.message?.description ||
          'Failed to upload profile picture. Please try again.',
        variant: 'destructive',
      })
    },
  })
}

/**
 * Hook to change password
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: (data: ChangePasswordRequest) => settingsApi.changePassword(data),
    onSuccess: (response) => {
      toast({
        title: response.message.title,
        description: response.message.description,
      })
    },
    onError: (error: any) => {
      toast({
        title: error.response?.data?.message?.title || 'Error',
        description:
          error.response?.data?.message?.description ||
          'Failed to change password. Please try again.',
        variant: 'destructive',
      })
    },
  })
}

/**
 * Hook to fetch notification settings
 */
export function useNotificationSettings() {
  return useQuery({
    queryKey: settingsKeys.notifications(),
    queryFn: () => settingsApi.getNotificationSettings(),
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Hook to update notification settings
 */
export function useUpdateNotificationSettings() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateNotificationSettingsRequest) =>
      settingsApi.updateNotificationSettings(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.notifications() })
      toast({
        title: response.message.title,
        description: response.message.description,
      })
    },
    onError: (error: any) => {
      toast({
        title: error.response?.data?.message?.title || 'Error',
        description:
          error.response?.data?.message?.description ||
          'Failed to update notification settings. Please try again.',
        variant: 'destructive',
      })
    },
  })
}

